/**
 * volume_medium — LOCAL VOLUMES as ONE MEDIUM (shader gaps program round 3, agent F, 2026-09-30). PURE (no three):
 * the composition oracle the harness (`verify:volumes`) proves, the scenario evaluators whose numbers the hand-back
 * quotes, the jitter-sequence evaluation under a TAA-like EMA, and the rules AtlasVolumetricLightModule applies.
 *
 * THE COMPOSITION LAW (Hillaire 2015 "Physically-based & unified volumetric rendering in Frostbite": the integrated
 * {scattering, transmittance} is a premultiplied colour/alpha pair; PBRT §11.1.3: τ is an integral of σt): along a
 * ray, a segment of constant σt and source q gives C = q·(1 − e^(−σt·L))/σt, T = e^(−σt·L) (the energy-conserving
 * "S − S·T over σt" of Hillaire's slide 29). Two DISJOINT segments compose front to back — C = C_f + T_f·C_b,
 * T = T_f·T_b — EXACTLY, for any split point: that is why cutting the local march at a fire's box entry
 * (ShadowedLightVolume's split) changes nothing where there is no fire. Two media OVERLAPPING on one segment add
 * their coefficients (σt = σ1 + σ2, q = q1 + q2 — "all the material parameters add, they are linear"): the
 * transmittances multiply, and from each medium's own (C, T) over the SAME segment the exact joint result is
 *   C = (ln T1 · E1 + ln T2 · E2) / (ln T1 + ln T2) · (1 − T1·T2),   E_i = C_i / (1 − T_i)   (homogeneous media)
 * — the per-medium "equilibrium radiance" E weighted by optical depth. Compositing one OVER the other instead
 * (C1 + T1·C2) is exact only when E1 = E2; `orderError` measures what it costs otherwise.
 */

// ── The oracle ───────────────────────────────────────────────────────────────────────────────────────────────────────
export interface CT { C: number; T: number }

/** One homogeneous segment: extinction σ (1/m), source q (radiance per metre), length L (m). */
export function segmentCT(sigma: number, q: number, L: number): CT {
  const s = Math.max(0, sigma);
  const T = Math.exp(-s * L);
  return { C: s < 1e-9 ? q * L : (q * (1 - T)) / s, T };
}

/** Front OVER back — disjoint segments along one ray. */
export function over(front: CT, back: CT): CT { return { C: front.C + front.T * back.C, T: front.T * back.T }; }

/** A heterogeneous ray: n exact constant-coefficient steps of [t0, t1] (the engine's march, without the jitter). */
export function marchRay(sample: (t: number) => { sigma: number; q: number }, t0: number, t1: number, n: number): CT {
  let acc: CT = { C: 0, T: 1 };
  const ds = (t1 - t0) / Math.max(1, n);
  for (let i = 0; i < n; i++) {
    const s = sample(t0 + (i + 0.5) * ds);
    acc = over(acc, segmentCT(s.sigma, s.q, ds));
  }
  return acc;
}

/** Two media coexisting over one segment: the exact joint integral (σ and q add). */
export function coexistExact(a: { sigma: number; q: number }, b: { sigma: number; q: number }, L: number): CT {
  return segmentCT(a.sigma + b.sigma, a.q + b.q, L);
}

/** The exact joint result from each medium's own (C, T) over the SAME segment (homogeneous media). */
export function combineSameSegment(a: CT, b: CT): CT {
  const T = a.T * b.T;
  const la = -Math.log(Math.max(1e-300, a.T)), lb = -Math.log(Math.max(1e-300, b.T));
  if (la + lb < 1e-12) return { C: a.C + b.C, T }; // both transparent: the sources just add
  const Ea = a.T < 1 - 1e-12 ? a.C / (1 - a.T) : a.C / Math.max(1e-12, la);
  const Eb = b.T < 1 - 1e-12 ? b.C / (1 - b.T) : b.C / Math.max(1e-12, lb);
  return { C: ((la * Ea + lb * Eb) / (la + lb)) * (1 - T), T };
}

/** How far "a OVER b" is from the exact joint result for two coexisting homogeneous media (relative to the joint C). */
export function orderError(a: { sigma: number; q: number }, b: { sigma: number; q: number }, L: number): { aOverB: number; bOverA: number } {
  const ex = coexistExact(a, b, L), A = segmentCT(a.sigma, a.q, L), B = segmentCT(b.sigma, b.q, L);
  const aOverB = over(A, B).C, bOverA = over(B, A).C;
  const d = Math.max(1e-12, ex.C);
  return { aOverB: (aOverB - ex.C) / d, bOverA: (bOverA - ex.C) / d };
}

// ── Scenario: a fire inside a haze (what the depth split fixes) ─────────────────────────────────────────────────────
export interface FireInHaze {
  /** The haze: extinction and source (per metre). */
  sigmaH: number; qH: number;
  /** The fire's box along the ray: entry distance and length; its own extinction and source inside. */
  tFire: number; lenFire: number; sigmaF: number; qF: number;
  /** The march end (the box exit or the opaque surface) and the background radiance behind it. */
  tEnd: number; bg: number;
}
export interface FireInHazeResult { reference: number; split: number; fireOverVolume: number; volumeOverFire: number; legacyAlpha: number }
/**
 * The pixel's radiance four ways: the REFERENCE (one joint march: haze everywhere, the fire's σ and q added inside
 * its box); the SPLIT (ShadowedLightVolume v3: front haze over the fire's own march over the back haze — the haze
 * inside the fire's box in the back layer); v2's order (the volume at renderOrder 1 over the background, the fire
 * over the whole volume); the reverse; and v2's actual output (its in-scatter additionally × (1 − T), the composite bug).
 */
export function fireInHaze(s: FireInHaze): FireInHazeResult {
  const n = 4000;
  const reference = over(marchRay((t) => {
    const inF = t >= s.tFire && t <= s.tFire + s.lenFire;
    return { sigma: s.sigmaH + (inF ? s.sigmaF : 0), q: s.qH + (inF ? s.qF : 0) };
  }, 0, s.tEnd, n), { C: s.bg, T: 1 }).C;
  const front = segmentCT(s.sigmaH, s.qH, s.tFire);
  const back = segmentCT(s.sigmaH, s.qH, s.tEnd - s.tFire);
  const fire = segmentCT(s.sigmaF, s.qF, s.lenFire);
  const bg: CT = { C: s.bg, T: 1 };
  const split = over(over(front, over(fire, back)), bg).C;
  const whole = segmentCT(s.sigmaH, s.qH, s.tEnd);
  const fireOverVolume = over(fire, over(whole, bg)).C;
  const volumeOverFire = over(whole, over(fire, bg)).C;
  const legacyAlpha = over(fire, over({ C: whole.C * (1 - whole.T), T: whole.T }, bg)).C;
  return { reference, split, fireOverVolume, volumeOverFire, legacyAlpha };
}

// ── Scenario: the post height fog applied at the OPAQUE depth (the coordinator's pass) ──────────────────────────────
/**
 * VolumetricFogPass is Beer–Lambert in distance with a visibility V (T = e^(−3.912·d/V) at eye height, a flat layer)
 * and runs AFTER the scene pass: a transparent (a fire, the local volume, smoke) is fogged as if it stood at the
 * OPAQUE surface behind it. Returns the factor it is dimmed by beyond what the fog in front of it would do:
 * T(t_obj, d_opaque) — 1 = right.
 */
export function heightFogOverAttenuation(visibilityM: number, tObject: number, dOpaque: number): number {
  const k = 3.912 / Math.max(1, visibilityM);
  return Math.exp(-k * Math.max(0, dOpaque - tObject));
}

// ── Jitter sequences under a TAA-like EMA ───────────────────────────────────────────────────────────────────────────
/**
 * The convergence question the STBN paper asks (EMA, α 0.1): each pixel estimates ∫₀¹ f(u) du from one sample u of
 * its sequence per frame, accumulated by an exponential moving average; returns the RMS error over the pixels after
 * each frame. `u(px, frame)` is the sequence.
 */
export function emaConvergence(u: (px: number, frame: number) => number, pixels: number, frames: number, f: (u: number) => number, exact: number, alpha = 0.1): number[] {
  const ema = new Float64Array(pixels);
  const out: number[] = [];
  for (let t = 0; t < frames; t++) {
    let s2 = 0;
    for (let p = 0; p < pixels; p++) {
      const v = f(u(p, t));
      ema[p] = t === 0 ? v : ema[p] + alpha * (v - ema[p]);
      s2 += (ema[p] - exact) ** 2;
    }
    out.push(Math.sqrt(s2 / pixels));
  }
  return out;
}

/**
 * The SPATIAL error pattern after k frames: the low-pass ratio (a 4×4 toroidal box) of the per-pixel error image
 * (`size` × `size`) — a blue (high-frequency) error is < 1, a white one ≈ 1, a clumpy one > 1. What the eye (and a
 * spatial denoiser) cares about beside the RMS.
 */
export function errorLowpassRatio(u: (x: number, y: number, frame: number) => number, size: number, frames: number, f: (u: number) => number, exact: number, alpha = 0.1): number {
  const n = size * size;
  const ema = new Float64Array(n);
  for (let t = 0; t < frames; t++) for (let i = 0; i < n; i++) {
    const v = f(u(i % size, Math.floor(i / size), t));
    ema[i] = t === 0 ? v : ema[i] + alpha * (v - ema[i]);
  }
  const e = new Float64Array(n);
  let mean = 0;
  for (let i = 0; i < n; i++) { e[i] = ema[i] - exact; mean += e[i]; }
  mean /= n;
  let vAll = 0;
  for (let i = 0; i < n; i++) vAll += (e[i] - mean) ** 2;
  vAll /= n;
  if (vAll <= 0) return 0;
  let v = 0;
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    let s = 0;
    for (let dy = 0; dy < 4; dy++) for (let dx = 0; dx < 4; dx++) s += e[((y + dy) % size) * size + ((x + dx) % size)] - mean;
    s /= 16; v += s * s;
  }
  return v / n / (vAll / 16);
}

// ── The module's rules ──────────────────────────────────────────────────────────────────────────────────────────────
export type VolumeResolution = 'full' | 'half';
/** 'golden' is the DEFAULT (unencumbered); 'stbn' is opt-in pending the owner's patent decision (impl-volumes.md). */
export type VolumeNoise = 'golden' | 'stbn' | 'white';
/** Split slots the module fills with the nearest fires inside the box (the engine allows up to 4). */
export const VOLUME_SPLIT_SLOTS = 2;
/** A fire counts for the split within this distance of the camera (its box must also lie inside the light box). */
export const VOLUME_SPLIT_RANGE_M = 45;
/** The front layer's renderOrder: after the fire volumes (9) and before the smoke cards (10+) and the bolts (15). */
export const VOLUME_FRONT_ORDER = 9.5;

export interface FireLike { rung?: string; at: readonly number[]; size: number }
export interface SplitBox { center: { x: number; y: number; z: number }; half: { x: number; y: number; z: number } }
/**
 * The split boxes (RENDER space) for the nearest ray-marched fires ('volume' / 'sim') inside the light box: the fire's
 * box is 1.3·size × 2.3·size, standing on the fire's point (lib/vfx/fire_rules fireBox), padded 5 cm. `at` is
 * ABSOLUTE; `scenePos` is scene.position (−origin) — render = absolute + scenePos. `boxMin/boxMax` are render space.
 */
export function fireSplitBoxes(fires: readonly FireLike[], camRender: { x: number; y: number; z: number }, scenePos: { x: number; y: number; z: number },
  boxMin: { x: number; y: number; z: number }, boxMax: { x: number; y: number; z: number }, slots = VOLUME_SPLIT_SLOTS, rangeM = VOLUME_SPLIT_RANGE_M): SplitBox[] {
  const out: Array<SplitBox & { d: number }> = [];
  for (const f of fires) {
    if (f.rung !== 'volume' && f.rung !== 'sim') continue;
    const w = 1.3 * f.size, h = 2.3 * f.size;
    const c = { x: f.at[0] + scenePos.x, y: f.at[1] + scenePos.y + h / 2, z: f.at[2] + scenePos.z };
    const half = { x: w / 2 + 0.05, y: h / 2 + 0.05, z: w / 2 + 0.05 };
    if (c.x + half.x < boxMin.x || c.x - half.x > boxMax.x || c.y + half.y < boxMin.y || c.y - half.y > boxMax.y || c.z + half.z < boxMin.z || c.z - half.z > boxMax.z) continue;
    const d = Math.hypot(c.x - camRender.x, c.y - camRender.y, c.z - camRender.z);
    if (d > rangeM) continue;
    out.push({ center: c, half, d });
  }
  out.sort((a, b) => a.d - b.d);
  return out.slice(0, Math.max(0, slots)).map(({ center, half }) => ({ center, half }));
}

/** Rect samples for the window lights in the volume: 4 when both the area lights and the volume are on, else 0. */
export function volumeRectSamplesFor(areaLights: boolean, killSwitch: boolean): 0 | 4 { return areaLights && !killSwitch ? 4 : 0; }

/** The engine mode a preset asks for — a change rebuilds the handle; a jitter change is a live uniform. */
export function volumeBuildKey(resolution: VolumeResolution, legacy: boolean, rect: number, steps: number): string {
  return legacy ? `legacy/${steps}` : `${resolution}/r${rect}/${steps}`;
}
